import { create } from 'zustand'
import { authService } from '../services/authService'
import type { AuthStatus, AuthUser, LoginResponse, UserRole } from '../types'

interface AuthState {
  // State
  user: AuthUser | null
  token: string | null
  status: AuthStatus

  // Computed-like getters
  isAuthenticated: boolean
  isAdmin: boolean

  // Actions
  login: (emailRut: string, password: string) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<void>
}

/**
 * Parse localStorage fields into an AuthUser object.
 * Returns null if required fields are missing.
 */
function hydrateUserFromStorage(): AuthUser | null {
  const userId = localStorage.getItem('user_id')
  const rut = localStorage.getItem('user_rut')
  const email = localStorage.getItem('user_email')
  const role = localStorage.getItem('user_role') as UserRole | null
  const companyId = localStorage.getItem('id_company')
  const branchOfficeId = localStorage.getItem('id_branch_office')

  if (!userId || !rut || !email || !role || !companyId || !branchOfficeId) {
    return null
  }

  return {
    userId: Number(userId),
    rut,
    email,
    role,
    companyId: Number(companyId),
    branchOfficeId: Number(branchOfficeId),
  }
}

/**
 * Persist all auth fields to localStorage (mirrors Angular behavior).
 */
function persistToStorage(response: LoginResponse): void {
  localStorage.setItem('user_id', String(response.user_id))
  localStorage.setItem('user_rut', response.user_rut)
  localStorage.setItem('user_email', response.user_email)
  localStorage.setItem('user_role', response.user_role)
  localStorage.setItem('id_company', String(response.id_company))
  localStorage.setItem('token', response.token)
  localStorage.setItem('id_branch_office', String(response.id_branch_office))
}

function clearStorage(): void {
  localStorage.removeItem('user_id')
  localStorage.removeItem('user_rut')
  localStorage.removeItem('user_email')
  localStorage.removeItem('user_role')
  localStorage.removeItem('id_company')
  localStorage.removeItem('token')
  localStorage.removeItem('id_branch_office')
}

/**
 * Map LoginResponse -> AuthUser
 */
function loginResponseToUser(response: LoginResponse): AuthUser {
  return {
    userId: response.user_id,
    rut: response.user_rut,
    email: response.user_email,
    role: response.user_role,
    companyId: response.id_company,
    branchOfficeId: response.id_branch_office,
  }
}

// Initial hydration from localStorage
const initialToken = localStorage.getItem('token')
const initialUser = hydrateUserFromStorage()

export const useAuthStore = create<AuthState>((set) => ({
  // Initial state — hydrate from localStorage
  user: initialUser,
  token: initialToken,
  status: initialToken ? 'checking' : 'not-authenticated',
  isAuthenticated: !!initialToken && !!initialUser,
  isAdmin: initialUser?.role === 'Admin',

  login: async (emailRut: string, password: string) => {
    const response = await authService.login(emailRut, password)
    persistToStorage(response)
    const user = loginResponseToUser(response)

    set({
      user,
      token: response.token,
      status: 'authenticated',
      isAuthenticated: true,
      isAdmin: user.role === 'Admin',
    })
  },

  logout: () => {
    clearStorage()
    set({
      user: null,
      token: null,
      status: 'not-authenticated',
      isAuthenticated: false,
      isAdmin: false,
    })
  },

  /**
   * Verify the stored token by calling GET /users/{id}.
   * Called once on app startup (in App.tsx or a top-level useEffect).
   */
  checkAuth: async () => {
    const token = localStorage.getItem('token')
    const userId = localStorage.getItem('user_id')

    if (!token || !userId) {
      clearStorage()
      set({
        user: null,
        token: null,
        status: 'not-authenticated',
        isAuthenticated: false,
        isAdmin: false,
      })
      return
    }

    try {
      const apiUser = await authService.getCurrentUser(Number(userId))
      const user: AuthUser = {
        userId: Number(apiUser.id),
        rut: apiUser.rut,
        email: apiUser.email,
        role: apiUser.role,
        companyId: apiUser.id_company,
        branchOfficeId: apiUser.id_branch_office,
      }

      // Refresh localStorage with latest data from server
      localStorage.setItem('user_rut', apiUser.rut)
      localStorage.setItem('user_email', apiUser.email)
      localStorage.setItem('user_role', apiUser.role)
      localStorage.setItem('id_company', String(apiUser.id_company))
      localStorage.setItem('id_branch_office', String(apiUser.id_branch_office))

      set({
        user,
        token,
        status: 'authenticated',
        isAuthenticated: true,
        isAdmin: user.role === 'Admin',
      })
    } catch {
      clearStorage()
      set({
        user: null,
        token: null,
        status: 'not-authenticated',
        isAuthenticated: false,
        isAdmin: false,
      })
    }
  },
}))
